/**
 * Embedded browser shell bridge (EMBEDDED_BROWSER_V1).
 *
 * The desktop shell hosts a real Chromium (CEF) view that renders BELOW the
 * wry webview — the page never receives pixels; it commands geometry. This
 * module wraps the shell's Tauri commands plus its events:
 *
 *   embedded_browser_show({x,y,w,h})   position + reveal (logical px, main-
 *                                      window content coords)
 *   embedded_browser_set_bounds(rect)  follow layout changes
 *   embedded_browser_hide()            pane closed / task switched away
 *   embedded_browser_status()          {ready, cdpPort} — cheap, and the
 *                                      shell lazy-inits CEF on its FIRST
 *                                      command, so this doubles as the wake
 *   embedded_browser_new_tab({url?})   tab strip mutators — SHELL-owned tabs,
 *   embedded_browser_activate_tab      never CDP Target.createTarget /
 *   embedded_browser_close_tab         closeTarget (poison law); close errs
 *     (both {targetId})                on the last tab (web hides its ✕)
 *   embedded-browser://state           {state:'ready'|'crash', cdpPort, detail}
 *   embedded-browser://tabs            {tabs:[{targetId, active}]} — which
 *                                      targets are shell tabs and which one
 *                                      is VISIBLE (a shell fact CDP can't tell)
 *
 * Everything goes through `window.__TAURI__.core.invoke` (withGlobalTauri —
 * same bridge as desktop-shell.ts) and is inert in a normal browser or in a
 * shell build that predates the commands: invoke rejections are swallowed
 * (surfaced as console.warn) — a missing command must never throw into a
 * render or a socket handler.
 */

interface TauriCore {
  invoke?: (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;
}
interface TauriEventApi {
  listen?: (
    event: string,
    handler: (e: { payload: unknown }) => void
  ) => Promise<() => void>;
}
interface TauriGlobal {
  core?: TauriCore;
  event?: TauriEventApi;
}

/** Pane rect in logical px, main-window content coordinates. The webview
 *  fills the window content area, so viewport coords ARE window coords. */
export interface EmbeddedBrowserRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface EmbeddedBrowserStatus {
  ready: boolean;
  cdpPort: number | null;
  /** True while the shell's loopback control channel is paired with the
   *  runner — the runner drives tab create/activate directly then, and the
   *  web's ensureTaskTab trigger stands down (task-tabs.ts). Older shells
   *  omit the field; it parses as false. */
  loopback: boolean;
}

/** Shell → page lifecycle event payload (`embedded-browser://state`). */
export interface EmbeddedBrowserStateEvent {
  state: "ready" | "crash";
  cdpPort?: number | null;
  detail?: string;
}

export const EMBEDDED_BROWSER_STATE_EVENT = "embedded-browser://state";

/**
 * Shell → page tab-set event payload (`embedded-browser://tabs`): which CDP
 * page targets are shell tabs and which one is visible. The runner's
 * BROWSER_TABS snapshot stays the source for title/url/favicon; this event
 * is the authoritative `active` marker (browserStore merges the two).
 */
export interface EmbeddedBrowserTabsEvent {
  /** title/url are the shell's LIVE display truth (its own CEF callbacks) —
   *  present from shells ≥ the strip-truth build, absent from older ones.
   *  The runner snapshot goes stale the moment its session closes (human
   *  navigation froze the strip, live 2026-07-16), so when the shell speaks,
   *  it wins. */
  tabs: Array<{
    targetId: string;
    active: boolean;
    title?: string;
    url?: string;
  }>;
}

export const EMBEDDED_BROWSER_TABS_EVENT = "embedded-browser://tabs";

/**
 * Shell → page surface hint (`embedded-browser://surface`): the agent is
 * browsing for this task — emitted live off the loopback channel's
 * surface.hint and re-emitted on main-window focus (an occluded webview
 * sleeps through live events). The web reacts exactly like a browser
 * tool-fire: open the Computer panel's Browser tab for the task.
 */
export interface EmbeddedBrowserSurfaceEvent {
  taskId: string;
}

export const EMBEDDED_BROWSER_SURFACE_EVENT = "embedded-browser://surface";

function tauriInvoke(
  win: Window
): ((cmd: string, args?: Record<string, unknown>) => Promise<unknown>) | null {
  const tauri = (win as unknown as { __TAURI__?: TauriGlobal }).__TAURI__;
  return tauri?.core?.invoke ?? null;
}

/** Fire-and-forget invoke — never throws into the caller. */
function invokeQuiet(
  cmd: string,
  args?: Record<string, unknown>,
  win: Window = window
): void {
  const invoke = tauriInvoke(win);
  if (!invoke) return;
  void invoke(cmd, args).catch((err: unknown) => {
    // eslint-disable-next-line no-console
    console.warn(`[EmbeddedBrowser] ${cmd} failed`, err);
  });
}

// Last rect the shell was told to paint at — the yield fallback (older shell
// without set_yield) needs it to re-show the pane at the right place.
let lastShownRect: EmbeddedBrowserRect | null = null;

// The Rust side is `fn(app, bounds: PaneBounds)` — one named struct param —
// so the rect nests under `bounds` (verified against feat/cef-embedded-browser).
export function embeddedBrowserShow(
  rect: EmbeddedBrowserRect,
  win: Window = window
): void {
  lastShownRect = { ...rect };
  invokeQuiet("embedded_browser_show", { bounds: { ...rect } }, win);
}

export function embeddedBrowserSetBounds(
  rect: EmbeddedBrowserRect,
  win: Window = window
): void {
  lastShownRect = { ...rect };
  invokeQuiet("embedded_browser_set_bounds", { bounds: { ...rect } }, win);
}

export function embeddedBrowserHide(win: Window = window): void {
  lastShownRect = null;
  invokeQuiet("embedded_browser_hide", undefined, win);
}

// ── Tab strip mutators ──────────────────────────────────────────────────────
// Shell-owned tabs (never CDP Target.createTarget / closeTarget — the poison
// law: closeTarget silently blanks the pane with no recovery event). Args are
// camelCase on the wire; the shell errs when the pane isn't created yet (the
// chrome only renders "+" once the shell reports live) and on last-tab close
// (the chrome hides ✕ when one tab remains) — invokeQuiet absorbs both.

export function embeddedBrowserNewTab(
  url?: string,
  win: Window = window
): void {
  invokeQuiet("embedded_browser_new_tab", url ? { url } : {}, win);
}

export function embeddedBrowserActivateTab(
  targetId: string,
  win: Window = window
): void {
  invokeQuiet("embedded_browser_activate_tab", { targetId }, win);
}

export function embeddedBrowserCloseTab(
  targetId: string,
  win: Window = window
): void {
  invokeQuiet("embedded_browser_close_tab", { targetId }, win);
}

// ── Human navigation (address bar + back/forward/reload) ────────────────────
// Drives the shell's ACTIVE-tab CEF browser DIRECTLY — never the agent's
// server→runner→CDP lane. That lane is deaf to a native dial runner, which is
// what left the URL bar inert (live 2026-07-14); local navigation works the
// instant the pane is up, independent of runner/server. `goto` carries a url.
export function embeddedBrowserNavigate(
  action: "goto" | "back" | "forward" | "reload",
  url?: string,
  win: Window = window
): void {
  invokeQuiet(
    "embedded_browser_navigate",
    url ? { action, url } : { action },
    win
  );
}

// ── Pane mount registry ─────────────────────────────────────────────────────
// The route-level hide guard (EmbeddedBrowserRouteGuard) needs one question
// answered after any navigation: is an EmbeddedBrowserPane still mounted? If
// not, the shell view must be hidden — the R2 finding was the pane surviving
// a task switch and floating over the New Task view. Count-based so React
// StrictMode double-mounts stay balanced.
let paneMountCount = 0;

export function registerEmbeddedPaneMount(): () => void {
  paneMountCount += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    paneMountCount -= 1;
  };
}

export function isEmbeddedPaneMounted(): boolean {
  return paneMountCount > 0;
}

// ── Overlay yield ───────────────────────────────────────────────────────────
// The shell's CEF view is a native child window — nothing the page renders
// can appear over its rect. Any overlay that must win (onboarding tour,
// command menu, dialogs) acquires a yield; while at least one is held the
// shell steps the pane back via embedded_browser_set_yield. Refcounted so
// stacked overlays release cleanly. Fallback for a shell build that predates
// the command: hide on acquire, re-show at the last rect on release.
let yieldCount = 0;

function invokeYield(yielding: boolean, win: Window): void {
  const invoke = tauriInvoke(win);
  if (!invoke) return;
  void invoke("embedded_browser_set_yield", { yielding }).catch(() => {
    // Older shell — degrade to hide/show. On release only re-show if the
    // pane still has a live rect (it may have unmounted mid-yield).
    if (yielding) {
      invokeQuiet("embedded_browser_hide", undefined, win);
    } else if (lastShownRect) {
      invokeQuiet(
        "embedded_browser_show",
        { bounds: { ...lastShownRect } },
        win
      );
    }
  });
}

export function acquireEmbeddedBrowserYield(win: Window = window): () => void {
  yieldCount += 1;
  if (yieldCount === 1) invokeYield(true, win);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    yieldCount -= 1;
    if (yieldCount === 0) invokeYield(false, win);
  };
}

/** Resolve the shell's pane status; null outside the shell or on any failure. */
export async function embeddedBrowserStatus(
  win: Window = window
): Promise<EmbeddedBrowserStatus | null> {
  const invoke = tauriInvoke(win);
  if (!invoke) return null;
  try {
    const raw = (await invoke("embedded_browser_status")) as
      | { ready?: unknown; cdpPort?: unknown; loopback?: unknown }
      | null
      | undefined;
    if (!raw || typeof raw.ready !== "boolean") return null;
    const status: EmbeddedBrowserStatus = {
      ready: raw.ready,
      cdpPort: typeof raw.cdpPort === "number" ? raw.cdpPort : null,
      loopback: raw.loopback === true,
    };
    lastKnownLoopback = status.loopback;
    return status;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[EmbeddedBrowser] embedded_browser_status failed", err);
    return null;
  }
}

// ── Status-wake (the chicken-and-egg breaker) ───────────────────────────────
// The shell lazy-inits CEF on its FIRST command, and the local runner's
// attach poll (10s) only succeeds once something woke the shell — but the
// page only learns the lane is live via BROWSER_LOCAL_READY, which itself
// needs the attach. So browser tool-use fires this wake unconditionally
// (flag-gated at the call site), deduped so a burst of browser tools costs
// one invoke per window.
const WAKE_DEDUPE_MS = 5_000;
let lastWakeAt = -Infinity;

// Last loopback pairing the shell reported (any status read refreshes it).
// The wake fires before every ensureTaskTab, so by the first HANDOFF this is
// current; unknown-yet parses as false, which degrades to the web trigger.
let lastKnownLoopback = false;

/** True when the shell last reported its loopback channel paired with the
 *  runner — the web's tab create/activate trigger stands down then. */
export function isEmbeddedLoopbackLive(): boolean {
  return lastKnownLoopback;
}

export function wakeEmbeddedBrowser(win: Window = window): void {
  const now = Date.now();
  if (now - lastWakeAt < WAKE_DEDUPE_MS) return;
  lastWakeAt = now;
  // The wake result doubles as the loopback-pairing read — never throws.
  void embeddedBrowserStatus(win).catch(() => null);
}

/** Test hook — re-arm the wake dedupe between cases. */
export function _resetEmbeddedWakeForTests(): void {
  lastWakeAt = -Infinity;
  lastKnownLoopback = false;
}

/** Test hook — clear the mount registry, yield refcount, and last rect. */
export function _resetEmbeddedBrowserForTests(): void {
  lastWakeAt = -Infinity;
  lastKnownLoopback = false;
  paneMountCount = 0;
  yieldCount = 0;
  lastShownRect = null;
}

/**
 * Subscribe to the shell's `embedded-browser://state` events. Returns an
 * unsubscribe fn; a no-op outside the shell. The listen promise resolves
 * async — unsubscribing before it settles still detaches (same track/
 * cancelled shape as the overlay pages).
 */
export function subscribeEmbeddedBrowserState(
  cb: (event: EmbeddedBrowserStateEvent) => void,
  win: Window = window
): () => void {
  const api = (win as unknown as { __TAURI__?: TauriGlobal }).__TAURI__?.event;
  if (!api?.listen) return () => {};
  let cancelled = false;
  let unlisten: (() => void) | null = null;
  void api
    .listen(EMBEDDED_BROWSER_STATE_EVENT, (e) => {
      if (cancelled) return;
      const payload = e.payload as EmbeddedBrowserStateEvent | undefined;
      if (payload?.state === "ready" || payload?.state === "crash") cb(payload);
    })
    .then((un) => {
      if (cancelled) un();
      else unlisten = un;
    })
    .catch(() => {});
  return () => {
    cancelled = true;
    unlisten?.();
  };
}

/**
 * Subscribe to the shell's `embedded-browser://surface` hints — same
 * lifecycle shape as subscribeEmbeddedBrowserState. Malformed payloads are
 * dropped.
 */
export function subscribeEmbeddedBrowserSurface(
  cb: (event: EmbeddedBrowserSurfaceEvent) => void,
  win: Window = window
): () => void {
  const api = (win as unknown as { __TAURI__?: TauriGlobal }).__TAURI__?.event;
  if (!api?.listen) return () => {};
  let cancelled = false;
  let unlisten: (() => void) | null = null;
  void api
    .listen(EMBEDDED_BROWSER_SURFACE_EVENT, (e) => {
      if (cancelled) return;
      const payload = e.payload as { taskId?: unknown } | undefined;
      if (typeof payload?.taskId === "string" && payload.taskId) {
        cb({ taskId: payload.taskId });
      }
    })
    .then((un) => {
      if (cancelled) un();
      else unlisten = un;
    })
    .catch(() => {});
  return () => {
    cancelled = true;
    unlisten?.();
  };
}

/**
 * Subscribe to the shell's `embedded-browser://tabs` events — same lifecycle
 * shape as subscribeEmbeddedBrowserState. Malformed payloads are dropped;
 * entries are filtered to well-formed {targetId, active} pairs so a partial
 * shell payload can never poison the store merge.
 */
export function subscribeEmbeddedBrowserTabs(
  cb: (event: EmbeddedBrowserTabsEvent) => void,
  win: Window = window
): () => void {
  const api = (win as unknown as { __TAURI__?: TauriGlobal }).__TAURI__?.event;
  if (!api?.listen) return () => {};
  let cancelled = false;
  let unlisten: (() => void) | null = null;
  void api
    .listen(EMBEDDED_BROWSER_TABS_EVENT, (e) => {
      if (cancelled) return;
      const payload = e.payload as { tabs?: unknown } | undefined;
      if (!payload || !Array.isArray(payload.tabs)) return;
      const tabs = payload.tabs
        .filter(
          (
            t
          ): t is {
            targetId: string;
            active: boolean;
            title?: unknown;
            url?: unknown;
          } =>
            !!t &&
            typeof (t as { targetId?: unknown }).targetId === "string" &&
            typeof (t as { active?: unknown }).active === "boolean"
        )
        .map((t) => ({
          targetId: t.targetId,
          active: t.active,
          // Live display meta from newer shells; undefined from older ones
          // (the store merge then falls back to the runner snapshot's rows).
          ...(typeof t.title === "string" ? { title: t.title } : {}),
          ...(typeof t.url === "string" ? { url: t.url } : {}),
        }));
      cb({ tabs });
    })
    .then((un) => {
      if (cancelled) un();
      else unlisten = un;
    })
    .catch(() => {});
  return () => {
    cancelled = true;
    unlisten?.();
  };
}
